import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useMetalRates } from '../context/MetalRatesContext';
import jsPDF from 'jspdf';

export default function SilverRatesPage() {
  const [bannerData, setBannerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const { user } = useAuth();
  const { metalRates: allRates } = useMetalRates();
  const bannerRef = useRef(null);

  // Derive silver rate from context
  const silverRate = allRates.find(r => r.metal_type?.toLowerCase() === 'silver')?.rate_per_gram || null;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const bannerRes = await api.get('/silver-banner');
      setBannerData(bannerRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pdfWidth - (margin * 2);
      
      // Background color
      pdf.setFillColor(6, 78, 59); // #1a3a5c
      pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');
      
      let yPos = 30;
      
      // Date and Silver Rate row
      const pillHeight = 8;
      const datePillWidth = 55;
      const ratePillWidth = 60; // Wider for the rate text
      
      // Date pill (left)
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(margin, yPos, datePillWidth, pillHeight, 4, 4, 'F');
      pdf.setTextColor(6, 78, 59);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      const dateText = bannerData.updated_at 
        ? `Date : ${new Date(bannerData.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase()}`
        : 'Not Updated Yet';
      pdf.text(dateText, margin + datePillWidth/2, yPos + pillHeight/2 + 1, { align: 'center' });
      
      // Silver Rate pill (right) - Use Rs. instead of rupee symbol for better font support
      const rateText = silverRate ? `Silver Rate : Rs.${silverRate.toLocaleString('en-IN')}/g` : 'Silver Rate : N/A';
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(pdfWidth - margin - ratePillWidth, yPos, ratePillWidth, pillHeight, 4, 4, 'F');
      pdf.text(rateText, pdfWidth - margin - ratePillWidth/2, yPos + pillHeight/2 + 1, { align: 'center' });
      
      yPos += 20;
      
      // Load and add logo image
      let logoLoaded = false;
      try {
        const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/marissa_logo.png`;
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        
        const logoDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        
        const logoWidth = 50;
        const logoHeight = 15;
        const logoX = (pdfWidth - logoWidth) / 2;
        pdf.addImage(logoDataUrl, 'PNG', logoX, yPos, logoWidth, logoHeight);
        logoLoaded = true;
        yPos += 25;
      } catch (logoError) {
        // Logo load failed - will use text fallback below
      }
      
      // Fallback to text if logo fails
      if (!logoLoaded) {
        pdf.setTextColor(201, 169, 103); // Gold color #C9A967
        pdf.setFontSize(24);
        pdf.setFont('helvetica', 'bolditalic');
        pdf.text('MARISSA', pdfWidth / 2, yPos + 5, { align: 'center' });
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('GOLD AND DIAMONDS', pdfWidth / 2, yPos + 13, { align: 'center' });
        yPos += 25;
      }
      
      // Rates table
      if (bannerData.rates && bannerData.rates.length > 0) {
        const rowHeight = 12;
        const colWidth = (contentWidth - 10) / 2;
        
        bannerData.rates.forEach((rate) => {
          // Weight box
          pdf.setFillColor(255, 255, 255);
          pdf.roundedRect(margin, yPos, colWidth, rowHeight, 2, 2, 'F');
          pdf.setTextColor(6, 78, 59);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${rate.weight} Gms`, margin + colWidth/2, yPos + rowHeight/2 + 1.5, { align: 'center' });
          
          // Amount box
          pdf.setFillColor(255, 255, 255);
          pdf.roundedRect(margin + colWidth + 10, yPos, colWidth, rowHeight, 2, 2, 'F');
          pdf.text(`${rate.amount.toLocaleString('en-IN')} Rs`, margin + colWidth + 10 + colWidth/2, yPos + rowHeight/2 + 1.5, { align: 'center' });
          
          yPos += rowHeight + 4;
        });
      }
      
      yPos += 10;
      
      // Footer notes
      pdf.setTextColor(180, 180, 180);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text('* Below 25 Gms, extra delivery charges applicable', pdfWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      pdf.setTextColor(140, 140, 140);
      pdf.text('* Prices are subject to change based on market rates', pdfWidth / 2, yPos, { align: 'center' });
      
      const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
      pdf.save(`Silver-Bar-Rates-${dateStr}.pdf`);
    } catch (error) {
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a3a5c] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!bannerData || !bannerData.is_visible) {
    return (
      <div className="min-h-screen bg-[#1a3a5c] flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl font-serif text-white mb-4">Silver Rates Currently Unavailable</h1>
        <Link 
          to="/" 
          className="text-[#C9A967] hover:underline flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a3a5c] py-8 px-4">
      {/* Back Button and Download */}
      <div className="max-w-lg mx-auto mb-3 flex justify-between items-center">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-xs"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to Store
        </Link>
        
        {/* Download PDF Button - Admin Only */}
        {user?.role === 'admin' && (
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
              downloading 
                ? 'bg-gray-400 cursor-not-allowed text-white' 
                : 'bg-[#C9A967] hover:bg-[#b8954d] text-[#1a3a5c]'
            }`}
          >
            {downloading ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-[#1a3a5c]"></div>
                <span>Generating...</span>
              </>
            ) : (
              <>
                <Download className="h-3 w-3" />
                <span>Download PDF</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Banner Card - Using table layout for better html2canvas compatibility */}
      <div 
        ref={bannerRef} 
        style={{
          maxWidth: '32rem',
          margin: '0 auto',
          backgroundColor: '#1a3a5c',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid rgba(201, 169, 103, 0.3)'
        }}
      >
        {/* Date and Silver Rate Row - Using table layout */}
        <table style={{ width: '100%', marginBottom: '16px', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ textAlign: 'left', padding: '0' }}>
                <span 
                  style={{ 
                    display: 'inline-block',
                    backgroundColor: 'rgba(255,255,255,0.9)', 
                    color: '#1a3a5c', 
                    padding: '6px 16px', 
                    borderRadius: '9999px', 
                    fontSize: '12px', 
                    fontWeight: '600',
                    letterSpacing: '0.05em'
                  }}
                >
                  {bannerData.updated_at 
                    ? `Date : ${new Date(bannerData.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase()}`
                    : 'Not Updated Yet'
                  }
                </span>
              </td>
              <td style={{ textAlign: 'right', padding: '0' }}>
                <span 
                  style={{ 
                    display: 'inline-block',
                    backgroundColor: 'rgba(255,255,255,0.9)', 
                    color: '#1a3a5c', 
                    padding: '6px 16px', 
                    borderRadius: '9999px', 
                    fontSize: '12px', 
                    fontWeight: '600',
                    letterSpacing: '0.05em'
                  }}
                >
                  {silverRate ? `Silver Rate : ₹${silverRate.toLocaleString('en-IN')}/g` : 'Silver Rate : N/A'}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Brand Logo */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img 
            src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/marissa_logo.png`}
            alt="Marissa Gold And Diamonds"
            style={{ height: '48px', margin: '0 auto', objectFit: 'contain' }}
            crossOrigin="anonymous"
          />
        </div>

        {/* Rates Table - Using actual table for perfect alignment */}
        {bannerData.rates && bannerData.rates.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '8px 10px' }}>
            <tbody>
              {bannerData.rates.map((rate) => (
                <tr key={`${rate.weight}-${rate.amount}`}>
                  <td 
                    style={{ 
                      width: '50%', 
                      padding: '8px 16px',
                      backgroundColor: 'rgba(255,255,255,0.95)', 
                      color: '#1a3a5c', 
                      borderRadius: '6px',
                      textAlign: 'center',
                      fontSize: '14px', 
                      fontWeight: '700'
                    }}
                  >
                    {rate.weight} Gms
                  </td>
                  <td 
                    style={{ 
                      width: '50%', 
                      padding: '8px 16px',
                      backgroundColor: 'rgba(255,255,255,0.95)', 
                      color: '#1a3a5c', 
                      borderRadius: '6px',
                      textAlign: 'center',
                      fontSize: '14px', 
                      fontWeight: '700'
                    }}
                  >
                    {rate.amount.toLocaleString('en-IN')} Rs
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>No rates available at the moment.</p>
          </div>
        )}

        {/* Footer Note */}
        <div style={{ marginTop: '24px', textAlign: 'center', width: '100%' }}>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '10px', marginBottom: '4px', textAlign: 'center', display: 'block', width: '100%' }}>
            * Below 25 Gms, extra delivery charges applicable
          </p>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', textAlign: 'center', display: 'block', width: '100%' }}>
            * Prices are subject to change based on market rates
          </p>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="max-w-lg mx-auto mt-6 text-center">
        <Link
          to="/products?category=silver"
          className="inline-flex items-center gap-2 bg-[#C9A967] text-[#1a3a5c] px-6 py-2 rounded-full font-semibold text-sm hover:bg-[#b8954d] transition-colors shadow-lg"
        >
          Browse Silver Collection
          <ArrowLeft className="h-4 w-4 rotate-180" />
        </Link>
      </div>
    </div>
  );
}
